import React, {useMemo, useState} from 'react';
import {Alert, Button} from '@betareader/design-system';
import * as Papa from 'papaparse';
import {useMutation, useQueryClient} from '@tanstack/react-query';
import {useTranslation} from 'react-i18next';
import {toast} from 'react-toastify';

import {useDispatch, useSelector} from '../../reduxHooks';
import {fetchReaders} from '../../modules/reader';
import {authUtil} from '../../utils';

type CSVRow = Record<string, unknown>;
export type ReaderInvitation = {
  email: string;
  external_id?: string;
  contentVersion?: string;
};
type CSVField = 'email' | 'external_id';
type Mapping = Record<CSVField, string>;
type UpdateProps = {idToken: string; bookId: string; invitations: ReaderInvitation[]};
type Props = {invitationSent(): void; disabled?: boolean};

const fieldNames: CSVField[] = ['email', 'external_id'];

const BulkInvitationForm: React.FC<Props> = ({invitationSent, disabled = false}) => {
  const {t} = useTranslation();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const currentBook = useSelector(state => state.book.currentBook);
  const updatingReader = useSelector(state => state.reader.updatingReader);
  const [csvData, setCSVData] = useState<CSVRow[]>([]);
  const [mapping, setMapping] = useState<Mapping | null>(null);
  const [edits, setEdits] = useState<Record<number, Partial<Record<CSVField, string>>>>({});
  const [contentVersion, setContentVersion] = useState<string | undefined>(
    currentBook.content[0]?._id
  );

  const {isPending: isLoading, mutateAsync} = useMutation({
    mutationFn: async ({idToken, bookId, invitations}: UpdateProps) => {
      const response = await fetch(
        `${process.env.REACT_APP_API_HOST}${process.env.REACT_APP_API_PATH}/books/${bookId}/readers`,
        {
          method: 'POST',
          headers: {'Content-Type': 'application/json', 'br-token': idToken},
          body: JSON.stringify({bulkInvitations: invitations})
        }
      );
      if (!response.ok) throw new Error('Bulk invitation failed');
      return response;
    },

    onSuccess: async () => {
      const idToken = await authUtil.getFreshIdToken();
      queryClient.invalidateQueries({
        queryKey: ['readers']
      });
      dispatch(fetchReaders(idToken, currentBook._id, []));
      invitationSent();
    }
  });

  const csvFields = useMemo(() => Object.keys(csvData[0] || {}), [csvData]);
  const valueFor = (row: CSVRow, index: number, field: CSVField) =>
    edits[index]?.[field] ?? String(row[mapping?.[field] || ''] ?? '');
  const invitations = csvData.map((row, index): ReaderInvitation => ({
    email: valueFor(row, index, 'email').trim(),
    external_id: valueFor(row, index, 'external_id').trim() || undefined,
    contentVersion
  }));
  const hasErrors = invitations.some(invitation =>
    !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(invitation.email));

  const handleFileLoaded = (rows: CSVRow[]) => {
    const first = rows[0];
    if (!first) {
      setCSVData([]);
      setMapping(null);
      setEdits({});
      return;
    }
    const keys = Object.keys(first);
    setMapping({
      email: keys.find(key => key.includes('mail')) || '',
      external_id: keys.find(key => key.includes('id')) || ''
    });
    setEdits({});
    setCSVData(rows);
  };

  const onFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    Papa.parse(file, {
      header: true,
      dynamicTyping: false,
      skipEmptyLines: true,
      transformHeader: (header: string) => header.toLowerCase().replace(/\W/g, '_'),
      complete: (results: {data: CSVRow[]; errors: unknown[]}) => {
        if (results.errors.length) {
          toast.error(t('SomethingWentWrong') as string);
          return;
        }
        handleFileLoaded(results.data);
      },
      error: () => toast.error(t('SomethingWentWrong') as string)
    });
  };

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (disabled || isLoading || hasErrors || invitations.length === 0) return;
    try {
      const idToken = await authUtil.getFreshIdToken();
      await mutateAsync({idToken, bookId: currentBook._id, invitations});
    } catch {
      toast.error(t('SomethingWentWrong') as string);
    }
  };

  return <form onSubmit={submit} aria-busy={Boolean(isLoading || updatingReader)}>
    <label className='br-field' htmlFor='reader_csv'>
      <span className='br-field__label'>{t('SelectCSVWithReaders')}</span>
    </label>
    <input id='reader_csv' name='Readers' type='file' accept='.csv,text/csv'
      disabled={disabled || isLoading} onChange={onFileChange} />
    {mapping && <>
      <label className='br-field'>
        <span className='br-field__label'>{t('SelectContentVersion')}</span>
        <select className='br-field__input' value={contentVersion || ''}
          disabled={disabled || isLoading}
          onChange={event => setContentVersion(event.target.value)}>
          {currentBook.content.map((version: any, index: number) =>
            <option key={version._id} value={version._id}>
              {`v${version.versionNumber || 1}`}
              {index === currentBook.content.length - 1 && ` (${t('latestVersion')})`}
            </option>)}
        </select>
      </label>
      <div style={{overflowX: 'auto'}}>
        <table className='br-table' aria-label={t('Readers')}>
          <thead><tr>
            {fieldNames.map(field => <th key={field} scope='col'>
              <label className='br-field'>
                <span className='br-field__label'>{field}</span>
                <select className='br-field__input' value={mapping[field]}
                  aria-label={`${t('MapCSVField')} ${field}`}
                  disabled={disabled || isLoading}
                  onChange={event => {
                    setMapping(current => current && {...current, [field]: event.target.value});
                    setEdits({});
                  }}>
                  <option value=''>{t('Ignore')}</option>
                  {csvFields.map(csvField => <option key={csvField} value={csvField}>
                    {csvField}
                  </option>)}
                </select>
              </label>
            </th>)}
            <th scope='col'>{t('Actions')}</th>
          </tr></thead>
          <tbody>
            {csvData.map((row, index) => <tr key={index}>
              {fieldNames.map(field => <td key={field}>
                <input className='br-field__input' type={field === 'email' ? 'email' : 'text'}
                  aria-label={`${field} ${index + 1}`}
                  disabled={disabled || isLoading}
                  value={valueFor(row, index, field)}
                  onChange={event => setEdits(current => ({
                    ...current,
                    [index]: {...current[index], [field]: event.target.value}
                  }))} />
              </td>)}
              <td><Button variant='ghost' size='small' type='button'
                disabled={disabled || isLoading || Boolean(updatingReader)}
                aria-label={`${t('Remove')} ${index + 1}`}
                onClick={() => {
                  setCSVData(current => current.filter((_, rowIndex) => rowIndex !== index));
                  setEdits({});
                }}>
                {t('Remove')}
              </Button></td>
            </tr>)}
          </tbody>
        </table>
      </div>
      {hasErrors && <Alert variant='error'>{t('InvalidEmail')}</Alert>}
      {csvData.length > 0 && <Button type='submit'
        disabled={disabled || hasErrors || isLoading || Boolean(updatingReader)}>
        {t('Send')}
      </Button>}
    </>}
  </form>;
};

export default BulkInvitationForm;
